import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const customEase = [0.16, 1, 0.3, 1];

export default function ControlShowcase() {
  const [minutes, setMinutes] = useState(60);
  const [preset, setPreset] = useState('60 Min');
  const [preRoll, setPreRoll] = useState('Off');
  const [cueText, setCueText] = useState('');

  const handlePresetClick = (p) => {
    setPreset(p);
    if (p === '30 Min') setMinutes(30);
    if (p === '45 Min') setMinutes(45);
    if (p === '60 Min') setMinutes(60);
  };

  const handleIncrement = () => {
    setMinutes(m => m + 1);
    setPreset('Custom Setup');
  };

  const handleDecrement = () => {
    setMinutes(m => Math.max(1, m - 1));
    setPreset('Custom Setup');
  };

  return (
    <motion.section 
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.4, ease: customEase }}
      className="w-full bg-[#0D0D0D] py-24 sm:py-32 px-4 sm:px-6 will-change-[transform,opacity]"
    >
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16 lg:gap-12">
        
        {/* Left Column (The Copy) - 45% on desktop */}
        <div className="w-full lg:w-[45%] flex flex-col justify-center">
          <h2 className="font-semibold text-[#FFFFFF] text-3xl sm:text-4xl mb-10 tracking-tight">
            Control the session.
          </h2>
          
          <div className="flex flex-col gap-8">
            <div className="border-l-2 border-[#27272A] pl-6 transition-all duration-200 ease-apple hover:border-white">
              <p className="text-[#D4D4D8] leading-[1.6] text-lg">
                <strong className="text-[#FFFFFF] font-bold">Duration.</strong> Tap a preset or set a custom runtime.
              </p>
            </div>
            
            <div className="border-l-2 border-[#27272A] pl-6 transition-all duration-200 ease-apple hover:border-white">
              <p className="text-[#D4D4D8] leading-[1.6] text-lg">
                <strong className="text-[#FFFFFF] font-bold">Pre-rolls.</strong> Run a countdown before you hit record.
              </p>
            </div>
            
            <div className="border-l-2 border-[#27272A] pl-6 transition-all duration-200 ease-apple hover:border-white">
              <p className="text-[#D4D4D8] leading-[1.6] text-lg">
                <strong className="text-[#FFFFFF] font-bold">Live cues.</strong> Send silent text updates straight to the screen.
              </p>
            </div>
          </div>
        </div>

        {/* Right Column (The Unified Interactive Surface) - 55% on desktop */}
        <div className="w-full lg:w-[55%] flex justify-center lg:justify-end">
          <div className="rounded-3xl border border-[#27272A] bg-[#050505] shadow-2xl shadow-black/80 w-full max-w-[480px] min-h-[500px] p-6 sm:p-8 flex flex-col relative overflow-hidden">
            
            {/* Header */}
            <div className="flex justify-between items-center text-[9px] font-bold tracking-widest text-[#A1A1AA] uppercase mb-8 sm:mb-12">
              <div className="flex items-center gap-2">
                <span>NOVA — IDLE</span>
              </div>
              <div className="flex items-center gap-5">
                <button className="flex items-center gap-1.5 hover:text-[#FFFFFF] transition-colors duration-150 ease-apple">
                  <SafeIcon icon={FiIcons.FiCopy} className="text-xs" />
                  <span className="hidden sm:inline">COPY LINK</span>
                </button>
                <button className="flex items-center gap-1.5 hover:text-[#FFFFFF] transition-colors duration-150 ease-apple">
                  <SafeIcon icon={FiIcons.FiExternalLink} className="text-xs" />
                  <span className="hidden sm:inline">OPEN DISPLAY</span>
                </button>
              </div>
            </div>

            <div className="flex-1 flex flex-col items-center w-full animate-fade-in">
              {/* Massive Main Timer */}
              <div className="flex justify-center items-center gap-4 sm:gap-5 mb-10 sm:mb-12">
                <div 
                  className="text-7xl sm:text-[96px] font-bold tracking-tighter leading-none text-white"
                  style={{ 
                    fontFamily: "'Roboto Mono', 'JetBrains Mono', 'SF Mono', 'Fira Code', 'Courier New', monospace",
                    fontVariantNumeric: "tabular-nums",
                    fontFeatureSettings: '"tnum" 1, "zero" 0, "ss01" 0'
                  }}
                >
                  {minutes.toString().padStart(2, '0')}:00
                </div>
                <div className="flex flex-col gap-2">
                  <button 
                    onClick={handleIncrement}
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#121212] border border-[#27272A] hover:bg-[#27272A] flex items-center justify-center text-[#D4D4D8] hover:text-white transition-all duration-150 ease-apple active:scale-[0.92]"
                  >
                    <SafeIcon icon={FiIcons.FiPlus} className="text-lg" />
                  </button>
                  <button 
                    onClick={handleDecrement}
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#121212] border border-[#27272A] hover:bg-[#27272A] flex items-center justify-center text-[#D4D4D8] hover:text-white transition-all duration-150 ease-apple active:scale-[0.92]"
                  >
                    <SafeIcon icon={FiIcons.FiMinus} className="text-lg" />
                  </button>
                </div>
              </div>

              {/* Controls Block */}
              <div className="w-full flex flex-col gap-6 sm:gap-8">
                
                {/* Duration Presets */}
                <div className="w-full flex flex-col gap-3">
                  <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA]">
                    DURATION PRESETS
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {['30 Min', '45 Min', '60 Min', 'Custom Setup'].map(p => (
                      <button
                        key={p}
                        onClick={() => handlePresetClick(p)}
                        className={`px-3 sm:px-4 py-2 rounded-full text-[11px] font-semibold transition-all duration-150 ease-apple active:scale-[0.96] border outline-none ${
                          preset === p 
                            ? 'bg-white text-black border-white shadow-[0_0_15px_rgba(255,255,255,0.1)]' 
                            : 'bg-[#121212] text-[#D4D4D8] border-[#27272A] hover:border-[#555]'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Session Settings */}
                <div className="w-full flex flex-col gap-3">
                  <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA]">
                    SESSION SETTINGS
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] font-semibold text-[#D4D4D8]">Pre-Roll</span>
                    <div className="flex items-center bg-[#121212] p-1 rounded-full border border-[#27272A] overflow-x-auto max-w-full">
                      {['Off', '3s', '5s', '10s', '30s', '60s'].map(pr => (
                        <button
                          key={pr}
                          onClick={() => setPreRoll(pr)}
                          className={`px-3 sm:px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-150 ease-apple active:scale-[0.96] whitespace-nowrap outline-none ${
                            preRoll === pr
                              ? 'bg-white text-black shadow-sm'
                              : 'text-[#D4D4D8] hover:text-white'
                          }`}
                        >
                          {pr}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Live Cues */}
                <div className="w-full flex flex-col gap-3">
                  <div className="flex justify-between items-center text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA]">
                    <span>LIVE CUES</span>
                    <span>{cueText.length}/40</span>
                  </div>
                  <div className="relative w-full">
                    <input
                      type="text"
                      value={cueText}
                      onChange={(e) => setCueText(e.target.value.slice(0, 40))}
                      placeholder="Push a live note to the display..."
                      className="w-full bg-[#121212] border border-[#27272A] rounded-xl py-3.5 pl-4 pr-10 text-[13px] text-white placeholder-[#555] outline-none focus:border-[#555] transition-all duration-150 ease-apple"
                    />
                    <button 
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#555] hover:text-white transition-colors duration-150 ease-apple active:scale-[0.90]"
                      onClick={() => setCueText('')}
                    >
                      <SafeIcon icon={FiIcons.FiSend} className="text-base" />
                    </button>
                  </div>
                </div>

                {/* Start Session Button */}
                <div className="w-full mt-2 sm:mt-4">
                  <button className="w-full bg-white text-black font-bold uppercase tracking-widest text-xs py-3.5 rounded-xl hover:bg-gray-200 transition-all duration-150 ease-apple active:scale-[0.98] outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black">
                    START SESSION
                  </button>
                </div>

              </div>
            </div>

          </div>
        </div>

      </div>
    </motion.section>
  );
}